import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  IconButton,
  Paper,
  Chip,
  styled,
  Autocomplete
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Camera as CameraIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00BCD4',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: '#00BCD4',
    },
  },
}));

const ReimbursementRequestForm = ({ onSubmit, vendors, isSubmitting }) => {
  // Form state
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().substring(0, 10));
  const [dateDue, setDateDue] = useState('');
  const [vendor, setVendor] = useState('');
  const [payTo, setPayTo] = useState('');
  const [returnTo, setReturnTo] = useState('');
  const [currencyType, setCurrencyType] = useState('USD');
  const [formOfPayment, setFormOfPayment] = useState('');
  const [charitablePurpose, setCharitablePurpose] = useState([]);
  const [entries, setEntries] = useState([
    { 
      location: '', 
      campaignGrant: '', 
      revenueExpenseClass: '', 
      itemId: '', 
      amount: '', 
      description: '' 
    }
  ]);

  // Entry handlers
  const handleEntryChange = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index][field] = value;
    setEntries(newEntries);
  };

  const handleAddEntry = () => {
    setEntries([
      ...entries,
      { 
        location: '', 
        campaignGrant: '', 
        revenueExpenseClass: '', 
        itemId: '', 
        amount: '', 
        description: '' 
      }
    ]);
  };

  const handleRemoveEntry = (index) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return entries.reduce((total, entry) => 
      total + (parseFloat(entry.amount) || 0), 0
    ).toFixed(2);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      transactionDate,
      dateDue,
      vendor,
      payTo,
      returnTo,
      currencyType,
      formOfPayment,
      charitablePurpose,
      entries
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <StyledTextField
            fullWidth
            type="date"
            label="Transaction Date"
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
            required
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <StyledTextField
            fullWidth
            type="date"
            label="Date Due"
            value={dateDue}
            onChange={(e) => setDateDue(e.target.value)}
            required
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            options={vendors || []}
            getOptionLabel={(option) => option.name}
            value={vendor}
            onChange={(_, newValue) => setVendor(newValue)}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                label="Vendor"
                required
              />
            )}
          />
        </Grid>

        {/* Currency and Payment */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Currency Type
            </InputLabel>
            <Select
              value={currencyType}
              onChange={(e) => setCurrencyType(e.target.value)}
              label="Currency Type"
              sx={{ color: '#fff' }}
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Entries Section */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Entries
          </Typography>
          
          {entries.map((entry, index) => (
            <Paper
              key={index}
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Location"
                    value={entry.location}
                    onChange={(e) => handleEntryChange(index, 'location', e.target.value)}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={entry.amount}
                    onChange={(e) => handleEntryChange(index, 'amount', e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    startIcon={<DeleteIcon />}
                    onClick={() => handleRemoveEntry(index)}
                    sx={{
                      color: '#ff4444',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 68, 68, 0.1)',
                      }
                    }}
                  >
                    Delete Entry
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddEntry}
            sx={{
              color: '#00BCD4',
              '&:hover': {
                backgroundColor: 'rgba(0, 188, 212, 0.1)',
              }
            }}
          >
            Add Entry
          </Button>
        </Grid>

        {/* Total and Submit */}
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ color: '#00BCD4', textAlign: 'right', mb: 2 }}>
            Total: ${calculateTotal()}
          </Typography>
          
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#00BCD4',
              '&:hover': {
                backgroundColor: '#00ACC1',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 188, 212, 0.5)',
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Reimbursement Request'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReimbursementRequestForm; 